/* eslint-disable prettier/prettier */
'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

import { cn } from 'utils/shadcn';
import { Button } from 'app/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'app/components/ui/command1';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'app/components/ui/popover';
import { useFormContext } from 'react-hook-form';
import { useProductsSlice } from 'app/pages/Product/slice';
import { useCourseSlice } from '../slice';

export default function Categories() {
  const { useLazyGetCourseCategoriesQuery } = useCourseSlice();
  const [getCategories, { data: categoriesData, error, isLoading }] =
    useLazyGetCourseCategoriesQuery();

  const form = useFormContext();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Fetch categories on search query change
  React.useEffect(() => {
    const fetchCategories = async () => {
      if (searchQuery.trim() === '') {
        await getCategories({});
      } else {
        await getCategories({ search: searchQuery });
      }
    };
    fetchCategories();
  }, [searchQuery]);

  // Handle adding a category
  const handleAddCategory = category => {
    const currentCategories = form.watch('categories') || [];

    // Check if category_id already exists in the currentCategories array using find
    const existingCategory = currentCategories.find(
      cat => cat?.category_id === category?.category_id,
    );

    if (!existingCategory) {
      const updatedCategories = [...currentCategories, category];
      form.setValue('categories', updatedCategories);
    }
  };

  // Handle adding a new category from search query
  const handleAddNewCategory = () => {
    if (searchQuery === '') return;

    const newCategories = form.getValues('newCategories') || [];
    const updatedNewCategories = [...newCategories, searchQuery];
    form.setValue('newCategories', updatedNewCategories);
    setSearchQuery('');
    setOpen(false);
  };

  // Handle removing a category
  const handleRemoveCategory = (id: number) => {
    const currentCategories = form.watch('categories') || [];
    const updatedCategories = currentCategories.filter(
      c => c.category_id !== id,
    );
    form.setValue('categories', updatedCategories);
  };

  // Handle removing a new category
  const handleRemoveNewCategory = (category: string) => {
    const newCategories = form.getValues('newCategories') || [];
    const updatedNewCategories = newCategories.filter(cat => cat !== category);
    form.setValue('newCategories', updatedNewCategories);
  };

  return (
    <>
      <div className="categories">
        <div className="font-medium text-[13px] mb-[6px]">Categories</div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full h-auto justify-between py-2"
            >
              <div className="flex gap-2 justify-start text-sm">
                Select Categories
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandInput
                placeholder="Search or Add Categories"
                onValueChange={val => setSearchQuery(val)}
                value={searchQuery}
              />
              <CommandEmpty>
                <div>No Category found.</div>
                {searchQuery !== '' && (
                  <Button className="mt-2" onClick={handleAddNewCategory}>
                    <Plus className="ml-1 w-4 h-4" />
                    Add
                  </Button>
                )}
              </CommandEmpty>

              <CommandGroup>
                <CommandList>
                  {categoriesData?.response?.data?.map(category => {
                    const selectedCategories = form.watch('categories') || [];
                    // Check if category_id is already selected using some
                    const isCategorySelected = selectedCategories.some(
                      cat => cat?.category_id === category?.category_id,
                    );

                    return (
                      <CommandItem
                        key={category.category_id}
                        value={category.category_name}
                        onSelect={() => handleAddCategory(category)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            isCategorySelected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        {category.category_name}
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="flex flex-wrap gap-2 justify-start my-3">
          {form.watch('categories')?.map(category => {
            return (
              <div
                key={category?.category_id}
                className="px-2 py-1 rounded-xl border bg-slate-200 text-xs font-medium"
              >
                {category?.category_name}{' '}
                <span
                  className="ml-1 cursor-pointer"
                  onClick={() => handleRemoveCategory(category?.category_id)}
                >
                  x
                </span>
              </div>
            );
          })}
          {form.watch('newCategories')?.map((category: string, i: number) => (
            <div
              key={i}
              className="px-2 py-1 rounded-xl border bg-slate-200 text-xs font-medium mb-3"
            >
              {category}{' '}
              <span
                className="ml-1 cursor-pointer"
                onClick={() => handleRemoveNewCategory(category)}
              >
                x
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
